import { CurrentLang } from '@/utils/lang';

type AccountActivationErrorTranslationKey =
  | 'errors.invalid_email'
  | 'errors.account_not_found'
  | 'errors.unavailable'
  | 'errors.invalid_code'
  | 'errors.rate_limited'
  | 'errors.generic';

interface OtpError {
  code?: string;
  details?: {
    code?: string;
  };
}

interface StoredAccountActivationChallenge {
  type: 'challenge';
  methodId: string;
  mode: 'signup' | 'login';
  expiresAt: number;
  resendAvailableAt: number;
}

interface StoredAccountActivationRateLimit {
  type: 'rate_limit';
  retryAvailableAt: number;
}

interface StoredAccountActivationDismissal {
  type: 'dismissed';
}

type StoredAccountActivationState =
  | StoredAccountActivationChallenge
  | StoredAccountActivationRateLimit
  | StoredAccountActivationDismissal;

function getSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStoredAccountActivation(key: string): StoredAccountActivationState | null {
  const storage = getSessionStorage();
  if (!storage || !key) return null;

  try {
    const value = JSON.parse(storage.getItem(key) || 'null') as StoredAccountActivationState | null;
    const expiredChallenge =
      value?.type === 'challenge' &&
      (!value.methodId ||
        !['signup', 'login'].includes(value.mode) ||
        !Number.isFinite(value.expiresAt) ||
        !Number.isFinite(value.resendAvailableAt) ||
        value.expiresAt <= Date.now());
    const expiredRateLimit =
      value?.type === 'rate_limit' &&
      (!Number.isFinite(value.retryAvailableAt) || value.retryAvailableAt <= Date.now());
    if (
      !value ||
      !['challenge', 'rate_limit', 'dismissed'].includes(value.type) ||
      expiredChallenge ||
      expiredRateLimit
    ) {
      storage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function storeAccountActivation(key: string, value: StoredAccountActivationState): void {
  const storage = getSessionStorage();
  if (!storage || !key) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Session persistence is optional; in-memory request guards remain active.
  }
}

export function removeStoredAccountActivation(key: string): void {
  const storage = getSessionStorage();
  if (!storage || !key) return;
  try {
    storage.removeItem(key);
  } catch {
    // A blocked storage backend must not affect authentication completion.
  }
}

export function accountActivationOtpLocale(): 'pt-br' | 'en' | 'es' {
  const language = CurrentLang().toLowerCase();
  if (language.startsWith('pt')) return 'pt-br';
  if (language.startsWith('es')) return 'es';
  return 'en';
}

export function accountActivationSemanticCode(error: unknown): string | undefined {
  const otpError = error as OtpError;
  return otpError?.details?.code || otpError?.code;
}

export function accountActivationErrorTranslationKey(
  error: unknown,
): AccountActivationErrorTranslationKey {
  switch (accountActivationSemanticCode(error)) {
    case 'EMAIL_OTP_INVALID_EMAIL':
      return 'errors.invalid_email';
    case 'EMAIL_OTP_ACCOUNT_NOT_FOUND':
      return 'errors.account_not_found';
    case 'EMAIL_AUTH_NOT_ALLOWED':
    case 'OTP_AUTH_METHOD_NOT_ALLOWED':
      return 'errors.unavailable';
    case 'OTP_INVALID_OR_EXPIRED':
      return 'errors.invalid_code';
    case 'OTP_RATE_LIMITED':
      return 'errors.rate_limited';
    default:
      return 'errors.generic';
  }
}

export type { AccountActivationErrorTranslationKey, StoredAccountActivationState };
